import { preprocessLines } from './preprocess';
import LOGO_DATA_URI from './logo';

const FIRM_NAME = 'Healthcare Management Consulting Group';
const FIRM_SHORT = 'HMCG';

function inlineFmt(s) {
  return s
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*([^*\n]+?)\*/g, '<em>$1</em>')
    .replace(/`([^`\n]+)`/g, '<code>$1</code>');
}

function mdToHtml(text) {
  const lines = preprocessLines(text.split('\n'));
  let html = '';
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    if (line.startsWith('|') && lines[i + 1] && /^\|[-: |]+\|/.test(lines[i + 1])) {
      const heads = line.split('|').slice(1, -1).map(h => h.trim());
      i += 2;
      const rows = [];
      while (i < lines.length && lines[i].startsWith('|')) {
        rows.push(lines[i].split('|').slice(1, -1).map(c => c.trim()));
        i++;
      }
      html += '<table><thead><tr>' + heads.map(h => `<th>${inlineFmt(h)}</th>`).join('') + '</tr></thead><tbody>';
      for (const row of rows) {
        html += '<tr>' + row.map(cell => `<td>${inlineFmt(cell)}</td>`).join('') + '</tr>';
      }
      html += '</tbody></table>';
      continue;
    }

    if (line.startsWith('#### ')) html += `<h4>${inlineFmt(line.slice(5))}</h4>`;
    else if (line.startsWith('### ')) html += `<h3>${inlineFmt(line.slice(4))}</h3>`;
    else if (line.startsWith('## ')) html += `<h2>${inlineFmt(line.slice(3))}</h2>`;
    else if (line.startsWith('# ')) html += `<h1>${inlineFmt(line.slice(2))}</h1>`;
    else if (/^---+$/.test(line.trim())) html += '<hr/>';
    else if (/^\s*[-*]\s+/.test(line) || /^\s*\d+\.\s+/.test(line)) {
      html += '<ul>';
      while (i < lines.length && (/^\s*[-*]\s+/.test(lines[i]) || /^\s*\d+\.\s+/.test(lines[i]))) {
        html += `<li>${inlineFmt(lines[i].replace(/^\s*[-*]\s+/, '').replace(/^\s*\d+\.\s+/, ''))}</li>`;
        i++;
      }
      html += '</ul>';
      continue;
    } else if (line.trim()) html += `<p>${inlineFmt(line)}</p>`;
    i++;
  }
  return html;
}

export function downloadReport(reportText, title, practiceName, date) {
  const bodyHtml = mdToHtml(reportText);

  const css = `
    body { font-family: Georgia, serif; color: #1a1a2e; margin: 0; padding: 0; font-size: 13px; line-height: 1.75; background: #fff; }
    .page { padding: 40px 56px 48px; }
    .cover-bar { display: flex; align-items: center; justify-content: space-between; border-bottom: 3px solid #2ec4b6; padding-bottom: 16px; margin-bottom: 28px; }
    .logo-img { height: 48px; width: auto; }
    .cover-right { text-align: right; font-size: 11px; color: #555; }
    .cover-right .firm { font-size: 13px; font-weight: 700; color: #0d5c6b; margin-bottom: 2px; }
    .cover-right .date { font-size: 11px; color: #888; }
    h1 { font-size: 22px; color: #0f1e2e; margin: 0 0 4px; line-height: 1.25; }
    h2 { font-size: 15px; color: #0d5c6b; border-bottom: 1px solid #b0d8dd; padding-bottom: 6px; margin-top: 30px; margin-bottom: 10px; }
    h3 { font-size: 10.5px; color: #7a6000; text-transform: uppercase; letter-spacing: .6px; margin-top: 18px; margin-bottom: 5px; }
    h4 { font-size: 13px; font-weight: 700; margin-top: 12px; margin-bottom: 4px; color: #1a1a2e; }
    p { margin: 0 0 9px; color: #333; }
    ul { padding-left: 20px; margin: 0 0 14px; }
    li { margin-bottom: 7px; color: #333; line-height: 1.7; }
    strong { font-weight: 700; color: #0f1e2e; }
    em { font-style: normal; font-weight: 600; color: #7a5c00; }
    code { background: #eef8f8; color: #0d5c6b; padding: 1px 5px; border-radius: 3px; font-size: 11px; font-family: monospace; }
    hr { border: none; border-top: 1px solid #cde; margin: 22px 0; }
    table { width: 100%; border-collapse: collapse; margin: 12px 0; font-size: 12px; page-break-inside: avoid; }
    th { background: #e8f7f7; color: #0d5c6b; text-align: left; padding: 7px 10px; font-size: 10px; font-weight: 700; text-transform: uppercase; border: 1px solid #b0d8dd; }
    td { padding: 6px 10px; border: 1px solid #cde; vertical-align: top; color: #333; }
    tr:nth-child(even) td { background: #f8fdfd; }
    .footer { font-size: 9px; color: #aaa; text-align: center; margin-top: 40px; border-top: 1px solid #eee; padding-top: 10px; }
    .footer strong { color: #0d5c6b; }
    @media print {
      body { padding: 0; }
      .page { padding: 20px 36px 32px; }
      h2 { page-break-before: auto; }
      table { page-break-inside: avoid; }
    }
  `;

  const fullHtml = `<!DOCTYPE html>
<html><head>
<meta charset="UTF-8">
<title>${title}</title>
<style>${css}</style>
</head><body>
<div class="page">
  <div class="cover-bar">
    <img class="logo-img" src="${LOGO_DATA_URI}" alt="${FIRM_SHORT}" />
    <div class="cover-right">
      <div class="firm">${FIRM_NAME}</div>
      <div class="date">Prepared: ${date}</div>
    </div>
  </div>
  ${bodyHtml}
  <div class="footer">
    Prepared by <strong>${FIRM_NAME} (${FIRM_SHORT})</strong> &nbsp;|&nbsp;
    This report was generated with AI-assisted analysis. Compliance observations are general in nature and do not constitute legal or regulatory advice.
  </div>
</div>
</body></html>`;

  const blob = new Blob([fullHtml], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = (practiceName || 'audit').replace(/[^a-z0-9]/gi, '-').toLowerCase() + '-website-audit.html';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
